import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Pencil,
  ChevronDown,
  ChevronRight,
  Copy,
  Star,
  Trash2,
  Calendar,
  Clock,
  MapPin,
  Users,
  Zap,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

/* ── Types ── */

interface ShowDayTemplateEvent {
  id: string
  templateId: string
  title: string
  category: string
  offsetMinutes: number
  durationMinutes: number
  whoAttends: string[]
  notes: string | null
  sortOrder: number
}

interface ShowDayTemplate {
  id: string
  artistId: string | null
  name: string
  isDefault: boolean
  events?: ShowDayTemplateEvent[]
  _count?: { events: number }
  createdAt: string
}

interface PreTourTemplate {
  id: string
  artistId: string | null
  name: string
  isDefault: boolean
  performanceRehearsalDays: number
  techRehearsalDays: number
  dressRehearsalDays: number
  firstRehearsalOffsetDays: number
  dailyStartTime: string
  dailyEndTime: string
  techCallTime: string
  performerCallTime: string
  notes: string | null
}

interface RehearsalSession {
  id: string
  title: string
  date: string
  startTime: string
  endTime: string
  venueName: string | null
  venueAddress: string | null
  notes: string | null
  showId: string | null
  _count?: { attendance: number }
}

interface Show {
  id: string
  title: string
  date: string
  venue: string
  city: string
}

interface Tour {
  id: string
  name: string
  status: string
}

/* ── Category color mapping ── */

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  load_in: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
  tech_rehearsal: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  sound_check: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  performance_rehearsal: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  dress_rehearsal: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
  doors: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  performance: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  meet_greet: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  load_out: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
}

/* ── Helpers ── */

function formatOffset(minutes: number): string {
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  const sign = minutes < 0 ? '-' : '+'
  if (h === 0) return `T${sign}${m}m`
  if (m === 0) return `T${sign}${h}h`
  return `T${sign}${h}h${m}m`
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString()
}

function categoryLabel(cat: string): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/* ── Main Page ── */

export function RehearsalsPage() {
  const [activeTab, setActiveTab] = useState<'templates' | 'pre-tour' | 'sessions'>('templates')

  return (
    <div>
      <PageHeader
        title="Rehearsals"
        subtitle="Show day templates, pre-tour rehearsal planning, and scheduled sessions."
      />

      {/* Tab navigation */}
      <div className="mb-6 flex gap-1 rounded-lg border border-ovation-ink/10 bg-white p-1">
        <TabButton
          active={activeTab === 'templates'}
          onClick={() => setActiveTab('templates')}
          label="Show Day Templates"
        />
        <TabButton
          active={activeTab === 'pre-tour'}
          onClick={() => setActiveTab('pre-tour')}
          label="Pre-Tour Rehearsals"
        />
        <TabButton
          active={activeTab === 'sessions'}
          onClick={() => setActiveTab('sessions')}
          label="Scheduled Sessions"
        />
      </div>

      {activeTab === 'templates' && <ShowDayTemplatesSection />}
      {activeTab === 'pre-tour' && <PreTourSection />}
      {activeTab === 'sessions' && <ScheduledSessionsSection />}
    </div>
  )
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'bg-ovation-ink text-ovation-paper'
          : 'text-ovation-muted hover:text-ovation-ink'
      }`}
    >
      {label}
    </button>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Section A: Show Day Templates
   ══════════════════════════════════════════════════════════════════════════════ */

function ShowDayTemplatesSection() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: templates, error, isLoading } = useQuery<ShowDayTemplate[], ApiClientError>({
    queryKey: ['show-day-templates'],
    queryFn: () => artistFetch<ShowDayTemplate[]>('/show-day-templates'),
  })

  const { data: shows } = useQuery<Show[]>({
    queryKey: ['shows-list'],
    queryFn: () => artistFetch<Show[]>('/shows'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl">Show Day Templates</h2>
        <button
          type="button"
          onClick={() => setCreating((c) => !c)}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-4 w-4" />
          {creating ? 'Cancel' : 'New template'}
        </button>
      </div>

      {creating && (
        <CreateTemplateForm
          templates={templates ?? []}
          onCreated={() => {
            setCreating(false)
            qc.invalidateQueries({ queryKey: ['show-day-templates'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        {templates && templates.length === 0 ? (
          <div className="rounded-lg border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No show day templates found.
          </div>
        ) : (
          <div className="space-y-3">
            {templates?.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                expanded={expandedId === t.id}
                onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                shows={shows ?? []}
              />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

function TemplateCard({
  template,
  expanded,
  onToggle,
  shows,
}: {
  template: ShowDayTemplate
  expanded: boolean
  onToggle: () => void
  shows: Show[]
}) {
  const qc = useQueryClient()
  const isSystem = template.artistId === null

  const { data: detail } = useQuery<ShowDayTemplate>({
    queryKey: ['show-day-template', template.id],
    queryFn: () => artistFetch<ShowDayTemplate>(`/show-day-templates/${template.id}`),
    enabled: expanded,
  })

  const cloneMut = useMutation({
    mutationFn: () =>
      artistFetch('/show-day-templates', {
        method: 'POST',
        body: { name: `${template.name} (Custom)`, cloneFrom: template.id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['show-day-templates'] }),
  })

  const setDefaultMut = useMutation({
    mutationFn: () =>
      artistFetch(`/show-day-templates/${template.id}`, {
        method: 'PATCH',
        body: { isDefault: true },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['show-day-templates'] }),
  })

  const deleteMut = useMutation({
    mutationFn: () =>
      artistFetch(`/show-day-templates/${template.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['show-day-templates'] }),
  })

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white">
      <div
        className="flex cursor-pointer items-center justify-between p-4"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-ovation-muted" />
          ) : (
            <ChevronRight className="h-4 w-4 text-ovation-muted" />
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-lg">{template.name}</span>
              {isSystem && (
                <span className="rounded bg-ovation-ink/5 px-2 py-0.5 text-xs text-ovation-muted">
                  System
                </span>
              )}
              {template.isDefault && (
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              )}
            </div>
            <span className="text-sm text-ovation-muted">
              {template._count?.events ?? 0} events
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {isSystem && (
            <button
              type="button"
              onClick={() => cloneMut.mutate()}
              disabled={cloneMut.isPending}
              className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-2 py-1 text-xs hover:bg-ovation-ink/5"
              title="Clone and customise"
            >
              <Copy className="h-3 w-3" /> Clone
            </button>
          )}
          {!isSystem && !template.isDefault && (
            <button
              type="button"
              onClick={() => setDefaultMut.mutate()}
              disabled={setDefaultMut.isPending}
              className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-2 py-1 text-xs hover:bg-ovation-ink/5"
              title="Set as default"
            >
              <Star className="h-3 w-3" /> Default
            </button>
          )}
          {!isSystem && (
            <button
              type="button"
              onClick={() => deleteMut.mutate()}
              disabled={deleteMut.isPending}
              className="flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
              title="Delete template"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {expanded && detail && (
        <div className="border-t border-ovation-ink/10 p-4">
          <EventTimeline events={detail.events ?? []} templateId={template.id} isSystem={isSystem} />
          <GenerateScheduleForShow templateId={template.id} shows={shows} />
        </div>
      )}
    </div>
  )
}

function EventTimeline({
  events,
  templateId,
  isSystem,
}: {
  events: ShowDayTemplateEvent[]
  templateId: string
  isSystem: boolean
}) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingEventId, setEditingEventId] = useState<string | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['show-day-template', templateId] })
    qc.invalidateQueries({ queryKey: ['show-day-templates'] })
  }

  const removeMut = useMutation({
    mutationFn: (eventId: string) =>
      artistFetch(`/show-day-templates/events/${eventId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium">Event Timeline (relative to show time)</h3>

      {events.length === 0 ? (
        <p className="text-sm text-ovation-muted">No events defined.</p>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => {
            if (editingEventId === ev.id) {
              return (
                <EventForm
                  key={ev.id}
                  mode="edit"
                  templateId={templateId}
                  eventId={ev.id}
                  initial={{
                    title: ev.title,
                    category: ev.category,
                    offsetMinutes: ev.offsetMinutes,
                    durationMinutes: ev.durationMinutes,
                  }}
                  onDone={() => {
                    setEditingEventId(null)
                    invalidate()
                  }}
                  onCancel={() => setEditingEventId(null)}
                />
              )
            }
            const colors = (CATEGORY_COLORS[ev.category] ?? CATEGORY_COLORS.load_in)!
            return (
              <div
                key={ev.id}
                className={`group relative inline-flex items-center gap-3 rounded-md border px-3 py-2 text-xs ${colors.bg} ${colors.text} ${colors.border}`}
              >
                <div>
                  <div className="font-medium">{ev.title}</div>
                  <div className="opacity-70">
                    {formatOffset(ev.offsetMinutes)} / {ev.durationMinutes}min
                  </div>
                </div>
                {!isSystem && (
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => setEditingEventId(ev.id)}
                      title="Edit times"
                      className="rounded p-1 hover:bg-ovation-ink/10"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMut.mutate(ev.id)}
                      title="Delete event"
                      className="rounded p-1 text-red-600 hover:bg-red-100"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!isSystem && (
        <>
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-1 text-xs text-ovation-muted hover:text-ovation-ink"
          >
            <Plus className="h-3 w-3" /> {adding ? 'Cancel' : 'Add event'}
          </button>
          {adding && (
            <EventForm
              mode="add"
              templateId={templateId}
              onDone={() => {
                setAdding(false)
                invalidate()
              }}
              onCancel={() => setAdding(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

function EventForm({
  mode,
  templateId,
  eventId,
  initial,
  onDone,
  onCancel,
}: {
  mode: 'add' | 'edit'
  templateId: string
  eventId?: string
  initial?: { title: string; category: string; offsetMinutes: number; durationMinutes: number }
  onDone: () => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [category, setCategory] = useState(initial?.category ?? 'sound_check')
  const [offsetMinutes, setOffsetMinutes] = useState(initial?.offsetMinutes ?? -240)
  const [durationMinutes, setDurationMinutes] = useState(initial?.durationMinutes ?? 60)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      if (mode === 'add') {
        await artistFetch(`/show-day-templates/${templateId}/events`, {
          method: 'POST',
          body: { title, category, offsetMinutes, durationMinutes, whoAttends: [] },
        })
      } else {
        await artistFetch(`/show-day-templates/events/${eventId}`, {
          method: 'PATCH',
          body: { title, category, offsetMinutes, durationMinutes },
        })
      }
      onDone()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  const categories = [
    'load_in', 'tech_rehearsal', 'sound_check', 'performance_rehearsal',
    'dress_rehearsal', 'doors', 'performance', 'meet_greet', 'load_out',
  ]

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3 rounded-md border border-ovation-ink/10 bg-ovation-ink/[0.02] p-3 md:grid-cols-4">
      <div>
        <label className="mb-1 block text-xs font-medium">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">Category</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
        >
          {categories.map((c) => (
            <option key={c} value={c}>{categoryLabel(c)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">Offset (min from show)</label>
        <input
          type="number"
          value={offsetMinutes}
          onChange={(e) => setOffsetMinutes(Number(e.target.value))}
          className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium">Duration (min)</label>
        <input
          type="number"
          value={durationMinutes}
          onChange={(e) => setDurationMinutes(Number(e.target.value))}
          min={1}
          className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
        />
      </div>
      {error && (
        <div className="col-span-full rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
          {error}
        </div>
      )}
      <div className="col-span-full flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : mode === 'add' ? 'Add event' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-ink/5"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function GenerateScheduleForShow({ templateId, shows }: { templateId: string; shows: Show[] }) {
  const qc = useQueryClient()
  const [showId, setShowId] = useState('')
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  async function generate() {
    if (!showId) return
    setGenerating(true)
    setResult(null)
    try {
      const res = await artistFetch<{ data: unknown[] }>(
        `/show-day-templates/${templateId}/generate/${showId}`,
        { method: 'POST' },
      )
      setResult(`Generated ${res.data.length} sessions.`)
      qc.invalidateQueries({ queryKey: ['rehearsals'] })
    } catch (err) {
      setResult(err instanceof ApiClientError ? err.message : 'Failed to generate.')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="mt-4 flex items-end gap-3 border-t border-ovation-ink/5 pt-4">
      <div className="flex-1">
        <label className="mb-1 block text-xs font-medium">Generate schedule for show</label>
        <select
          value={showId}
          onChange={(e) => setShowId(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
        >
          <option value="">Select a show...</option>
          {shows.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title} ({formatDate(s.date)})
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={generate}
        disabled={!showId || generating}
        className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
      >
        <Zap className="h-3 w-3" /> {generating ? 'Generating...' : 'Generate'}
      </button>
      {result && <span className="text-xs text-ovation-muted">{result}</span>}
    </div>
  )
}

function CreateTemplateForm({
  templates,
  onCreated,
}: {
  templates: ShowDayTemplate[]
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [cloneFrom, setCloneFrom] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await artistFetch('/show-day-templates', {
        method: 'POST',
        body: { name, cloneFrom: cloneFrom || undefined },
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium">Template name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Festival Set, Theatre Show"
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Clone from (optional)</label>
          <select
            value={cloneFrom}
            onChange={(e) => setCloneFrom(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">Start blank</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Creating...' : 'Create template'}
      </button>
    </form>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Section B: Pre-Tour Rehearsals
   ══════════════════════════════════════════════════════════════════════════════ */

function PreTourSection() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)

  const { data: templates, error, isLoading } = useQuery<PreTourTemplate[], ApiClientError>({
    queryKey: ['pre-tour-templates'],
    queryFn: () =>
      artistFetch<PreTourTemplate[]>('/show-day-templates/pre-tour'),
  })

  const { data: tours } = useQuery<Tour[]>({
    queryKey: ['tours-list'],
    queryFn: () => artistFetch<Tour[]>('/tours'),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl">Pre-Tour Rehearsal Templates</h2>
        <button
          type="button"
          onClick={() => setCreating((c) => !c)}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-4 w-4" />
          {creating ? 'Cancel' : 'New template'}
        </button>
      </div>

      {creating && (
        <CreatePreTourForm
          templates={templates ?? []}
          onCreated={() => {
            setCreating(false)
            qc.invalidateQueries({ queryKey: ['pre-tour-templates'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        {templates && templates.length === 0 ? (
          <div className="rounded-lg border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No pre-tour rehearsal templates found.
          </div>
        ) : (
          <div className="space-y-3">
            {templates?.map((t) => (
              <PreTourTemplateCard key={t.id} template={t} tours={tours ?? []} />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

function PreTourTemplateCard({ template, tours }: { template: PreTourTemplate; tours: Tour[] }) {
  const qc = useQueryClient()
  const isSystem = template.artistId === null
  const [editing, setEditing] = useState(false)
  const [tourId, setTourId] = useState('')
  const [genResult, setGenResult] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)

  const deleteMut = useMutation({
    mutationFn: () =>
      artistFetch(`/show-day-templates/pre-tour/${template.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pre-tour-templates'] }),
  })

  const setDefaultMut = useMutation({
    mutationFn: () =>
      artistFetch(`/show-day-templates/pre-tour/${template.id}`, {
        method: 'PATCH',
        body: { isDefault: true },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pre-tour-templates'] }),
  })

  async function generate() {
    if (!tourId) return
    setGenerating(true)
    setGenResult(null)
    try {
      const res = await artistFetch<{ data: unknown[] }>(
        `/show-day-templates/pre-tour/${template.id}/generate/${tourId}`,
        { method: 'POST' },
      )
      setGenResult(`Generated ${res.data.length} sessions.`)
      qc.invalidateQueries({ queryKey: ['rehearsals'] })
    } catch (err) {
      setGenResult(err instanceof ApiClientError ? err.message : 'Failed to generate.')
    } finally {
      setGenerating(false)
    }
  }

  const totalDays = template.performanceRehearsalDays + template.techRehearsalDays + template.dressRehearsalDays

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-lg">{template.name}</span>
            {isSystem && (
              <span className="rounded bg-ovation-ink/5 px-2 py-0.5 text-xs text-ovation-muted">System</span>
            )}
            {template.isDefault && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ovation-muted">
            <span>{totalDays} days total ({template.performanceRehearsalDays} perf + {template.techRehearsalDays} tech + {template.dressRehearsalDays} dress)</span>
            <span>Starts {template.firstRehearsalOffsetDays}d before first show</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-ovation-muted">
            <span>Daily: {template.dailyStartTime} - {template.dailyEndTime}</span>
            <span>Tech call: {template.techCallTime}</span>
            <span>Performer call: {template.performerCallTime}</span>
          </div>
          {template.notes && (
            <p className="mt-2 text-xs text-ovation-muted">{template.notes}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isSystem && !template.isDefault && (
            <button
              type="button"
              onClick={() => setDefaultMut.mutate()}
              disabled={setDefaultMut.isPending}
              className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-2 py-1 text-xs hover:bg-ovation-ink/5"
            >
              <Star className="h-3 w-3" /> Default
            </button>
          )}
          {!isSystem && (
            <button
              type="button"
              onClick={() => deleteMut.mutate()}
              disabled={deleteMut.isPending}
              className="flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Generate for tour */}
      <div className="mt-3 flex items-end gap-3 border-t border-ovation-ink/5 pt-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium">Generate for tour</label>
          <select
            value={tourId}
            onChange={(e) => setTourId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-xs"
          >
            <option value="">Select a tour...</option>
            {tours.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.status})</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={!tourId || generating}
          className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          <Zap className="h-3 w-3" /> {generating ? 'Generating...' : 'Generate'}
        </button>
        {genResult && <span className="text-xs text-ovation-muted">{genResult}</span>}
      </div>
    </div>
  )
}

function CreatePreTourForm({
  templates,
  onCreated,
}: {
  templates: PreTourTemplate[]
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [cloneFrom, setCloneFrom] = useState('')
  const [performanceRehearsalDays, setPerformanceRehearsalDays] = useState(3)
  const [techRehearsalDays, setTechRehearsalDays] = useState(1)
  const [dressRehearsalDays, setDressRehearsalDays] = useState(1)
  const [firstRehearsalOffsetDays, setFirstRehearsalOffsetDays] = useState(5)
  const [dailyStartTime, setDailyStartTime] = useState('10:00')
  const [dailyEndTime, setDailyEndTime] = useState('18:00')
  const [techCallTime, setTechCallTime] = useState('14:00')
  const [performerCallTime, setPerformerCallTime] = useState('10:00')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await artistFetch('/show-day-templates/pre-tour', {
        method: 'POST',
        body: {
          name,
          cloneFrom: cloneFrom || undefined,
          performanceRehearsalDays,
          techRehearsalDays,
          dressRehearsalDays,
          firstRehearsalOffsetDays,
          dailyStartTime,
          dailyEndTime,
          techCallTime,
          performerCallTime,
        },
      })
      onCreated()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium">Template name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Clone from (optional)</label>
          <select
            value={cloneFrom}
            onChange={(e) => setCloneFrom(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">Start fresh</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium">Performance days</label>
          <input type="number" value={performanceRehearsalDays} onChange={(e) => setPerformanceRehearsalDays(Number(e.target.value))} min={0} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Tech days</label>
          <input type="number" value={techRehearsalDays} onChange={(e) => setTechRehearsalDays(Number(e.target.value))} min={0} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Dress days</label>
          <input type="number" value={dressRehearsalDays} onChange={(e) => setDressRehearsalDays(Number(e.target.value))} min={0} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Offset (days before show)</label>
          <input type="number" value={firstRehearsalOffsetDays} onChange={(e) => setFirstRehearsalOffsetDays(Number(e.target.value))} min={1} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium">Daily start</label>
          <input type="time" value={dailyStartTime} onChange={(e) => setDailyStartTime(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Daily end</label>
          <input type="time" value={dailyEndTime} onChange={(e) => setDailyEndTime(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Tech call</label>
          <input type="time" value={techCallTime} onChange={(e) => setTechCallTime(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Performer call</label>
          <input type="time" value={performerCallTime} onChange={(e) => setPerformerCallTime(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-2 py-1.5 text-sm" />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Creating...' : 'Create template'}
      </button>
    </form>
  )
}

/* ══════════════════════════════════════════════════════════════════════════════
   Section C: Scheduled Sessions
   ══════════════════════════════════════════════════════════════════════════════ */

function ScheduledSessionsSection() {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)

  const { data: sessions, error, isLoading } = useQuery<RehearsalSession[], ApiClientError>({
    queryKey: ['rehearsals'],
    queryFn: async () => {
      const res = await artistFetch<{ data: RehearsalSession[] } | RehearsalSession[]>('/rehearsals')
      if (Array.isArray(res)) return res
      return (res as { data: RehearsalSession[] }).data ?? []
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => artistFetch(`/rehearsals/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rehearsals'] }),
  })

  // Group sessions by date
  const grouped: Record<string, RehearsalSession[]> = {}
  if (sessions) {
    for (const s of sessions) {
      const key = new Date(s.date).toISOString().slice(0, 10)
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(s)
    }
  }
  const sortedDates = Object.keys(grouped).sort()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl">Scheduled Sessions</h2>
        <button
          type="button"
          onClick={() => setCreating((c) => !c)}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
        >
          <Plus className="h-4 w-4" />
          {creating ? 'Cancel' : 'Add session'}
        </button>
      </div>

      {creating && (
        <CreateSessionForm
          onCreated={() => {
            setCreating(false)
            qc.invalidateQueries({ queryKey: ['rehearsals'] })
          }}
        />
      )}

      <AsyncBoundary isLoading={isLoading} error={error}>
        {sortedDates.length === 0 ? (
          <div className="rounded-lg border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No rehearsal sessions scheduled yet.
          </div>
        ) : (
          <div className="space-y-4">
            {sortedDates.map((dateKey) => (
              <div key={dateKey}>
                <div className="mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-ovation-muted" />
                  <h3 className="text-sm font-medium">
                    {new Date(dateKey + 'T00:00:00').toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </h3>
                </div>
                <div className="space-y-2 pl-6">
                  {grouped[dateKey]!.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-start justify-between rounded-lg border border-ovation-ink/10 bg-white p-3"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{s.title}</span>
                          {s.showId && (
                            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-700">
                              Show linked
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-ovation-muted">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(s.startTime)} - {formatTime(s.endTime)}
                          </span>
                          {s.venueName && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {s.venueName}
                            </span>
                          )}
                          {s._count && s._count.attendance > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {s._count.attendance} crew
                            </span>
                          )}
                        </div>
                        {s.notes && (
                          <p className="mt-1 text-xs text-ovation-muted">{s.notes}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteMut.mutate(s.id)}
                        disabled={deleteMut.isPending}
                        className="rounded-md p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                        title="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

function CreateSessionForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [startTime, setStartTime] = useState('14:00')
  const [endTime, setEndTime] = useState('18:00')
  const [venueName, setVenueName] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const start = new Date(`${date}T${startTime}:00`)
      const end = new Date(`${date}T${endTime}:00`)
      await artistFetch('/rehearsals', {
        method: 'POST',
        body: {
          title,
          date: new Date(date).toISOString(),
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          venueName: venueName || undefined,
          notes: notes || undefined,
          expectedCrew: [],
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-4"
    >
      <div>
        <label className="mb-1 block text-sm font-medium">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Start</label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">End</label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Venue (optional)</label>
        <input
          type="text"
          value={venueName}
          onChange={(e) => setVenueName(e.target.value)}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
        />
      </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Creating...' : 'Create session'}
      </button>
    </form>
  )
}
